import type { ComponentType } from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { NOT_SIGNED_IN, UserContext } from '../../contexts';
import type { PlainObject } from 'simplytyped';
import { allPaths } from '../../routes/Paths';
import { throttle } from 'lodash';
import toast from 'react-hot-toast';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const REFRESH_ME = 4 * 60 * 1000;
const LAST_ACTIVITY_KEY = 'lastActivity';

export let inactivityToastError: string | null = null;
const withAuthRedirect =
  <Props extends PlainObject>(Component: ComponentType<Props>) =>
  (props: Props & { currentPage?: string }) => {
    const { user, resetLoginData, resetUser, logOut, refreshToken } =
      useContext(UserContext);
    const [initialCheck, setInitialCheck] = useState(false);

    const logout = useCallback(() => {
      if (user.merchantId !== NOT_SIGNED_IN) {
        inactivityToastError = toast.error('Logged out due to inactivity', {
          duration: Infinity,
          position: 'top-left'
        });
        // TODO log
        //console.log('Logging out ...');
        resetLoginData();
        resetUser();
        logOut();
        localStorage.removeItem(LAST_ACTIVITY_KEY);

        if (window.location.pathname !== allPaths.LOGIN.path) {
          // If the user timeouts during 2FA / challenges etc. then redirect them.
          return <Navigate replace to="/login" />;
        }
      } else {
        // TODO log
        //console.log('Not logging out ...');
      }
    }, [resetLoginData, resetUser, logOut, user]);

    const setLastActivity = useCallback(() => {
      // TODO log
      //console.log('set activity', Date.now().toString());
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    }, []);

    const checkActivity = useCallback(async () => {
      const lastActivity = parseInt(
        localStorage.getItem(LAST_ACTIVITY_KEY) || '0'
      );

      if (Date.now() - lastActivity > INACTIVITY_TIMEOUT_MS) {
        // TODO log
        //console.log('Check activity LOG OUT');
        await logout();
      } else {
        // TODO log
        //console.log('Check activity Refresh');
        await refreshToken();
      }
    }, [logout, refreshToken]);

    useEffect(() => {
      // Events to reset the timer on
      const events = ['mousemove', 'keydown', 'load', 'focus'];
      const resetTimer = throttle(() => {
        setLastActivity();
      }, 5 * 1000);

      events.forEach((event) => window.addEventListener(event, resetTimer));

      // Set a regular check in case the tab stays open without interaction
      const intervalId = setInterval(checkActivity, REFRESH_ME);

      return () => {
        events.forEach((event) =>
          window.removeEventListener(event, resetTimer)
        );
        clearInterval(intervalId);
      };
    }, [setLastActivity, checkActivity]);

    useEffect(() => {
      // To handle close tab scenarios -----
      if (initialCheck === false) {
        setInitialCheck(true);
        checkActivity();
      }
    }, [checkActivity, initialCheck, setInitialCheck]);

    if (user.userType === 'unknown') {
      // TODO: keep this for testing but remove before MVP
      // TODO log
      // console.log('___redirect: userType unknown');
      return <Navigate replace to="/login" />;
    }

    return <Component {...props} />;
  };

export { withAuthRedirect };
